import React from "react";
import { v4 as uuid } from "uuid";
import moment from "moment-timezone";
import FullCalendar from "@fullcalendar/react";
import { Duration, EventClickArg, EventDropArg } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin, {
  DateClickArg,
  EventResizeDoneArg,
} from "@fullcalendar/interaction";

import DayHeaderContent from "./DayHeaderContent";
import SlotLabelContent from "./SlotLabelContent";

import styles from "./TimeIntervalPicker.module.scss";

export interface TimeIntervalData {
  id: string;
  title: string;
  start: Date;
  end: Date;
  duration?: Duration;
  backgroundColor?: string;
  borderColor?: string;
  textColor?: string;
  editable: boolean;
}

const templateEvent = {
  id: uuid(),
  periodTitle: "Period",
  start: new Date(),
  end: new Date(),
  editable: true,
  backgroundColor: "#818CF8",
  borderColor: "#818CF8",
};

interface TimeIntervalPickerProps {
  timeIntervals: TimeIntervalData[];
  setTimeIntervals: (data: TimeIntervalData[]) => void;
  periodTitle: string;
  editable?: boolean;
}

export const getEventData = (
  e: EventDropArg | EventResizeDoneArg,
): TimeIntervalData => {
  return {
    id: e.event.id,
    title: e.event.title,
    start: moment(e.event.start).toDate(),
    end: moment(e.event.end).toDate(),
    editable: e.event.startEditable,
  };
};

export function convertDateTimeStringsToEventData(
  dateTimeStrings: string[][],
): TimeIntervalData[] {
  const events: TimeIntervalData[] = [];
  const now = new Date();
  const weekStart = new Date(
    now.getFullYear(),
    now.getMonth(),
    now.getDate() - now.getDay(),
  );
  const weekEnd = new Date(
    now.getFullYear(),
    now.getMonth(),
    weekStart.getDate() + 6,
  );

  for (let i = 0; i < dateTimeStrings.length; i += 1) {
    const start = new Date(dateTimeStrings[i][0]);
    const end = new Date(dateTimeStrings[i][1]);

    // Adjust start and end dates to the same day of the week within the current week
    const dayOfWeek = start.getDay();
    const adjustedStart = new Date(weekStart);
    adjustedStart.setDate(adjustedStart.getDate() + dayOfWeek);
    adjustedStart.setHours(
      start.getHours(),
      start.getMinutes(),
      start.getSeconds(),
      start.getMilliseconds(),
    );

    const adjustedEnd = new Date(weekStart);
    adjustedEnd.setDate(adjustedEnd.getDate() + dayOfWeek);
    adjustedEnd.setHours(
      end.getHours(),
      end.getMinutes(),
      end.getSeconds(),
      end.getMilliseconds(),
    );

    // If the adjusted start and end dates are still outside of the current week, skip this event
    if (adjustedStart < weekStart || adjustedEnd > weekEnd) {
      continue;
    }

    const id = uuid();
    const event: TimeIntervalData = {
      id,
      title: "Non-Use",
      start: adjustedStart,
      end: adjustedEnd,
      editable: true,
    };
    events.push(event);
  }
  return events;
}

const TimeIntervalPicker = ({
  timeIntervals,
  setTimeIntervals,
  periodTitle,
  editable = true,
}: TimeIntervalPickerProps) => {
  const handleDateClick = (e: DateClickArg) => {
    const startTime = moment(e.dateStr).toDate();
    const endTime = moment(e.dateStr).add(30, "m").toDate();
    const newEvent = {
      ...templateEvent,
      id: uuid(),
      start: startTime,
      end: endTime,
      title: periodTitle,
    };
    setTimeIntervals([...timeIntervals, newEvent]);
  };

  const handleEventClick = (e: EventClickArg) => {
    setTimeIntervals(timeIntervals.filter((event) => event.id !== e.event.id));
  };

  const onSaveEvent = (data: TimeIntervalData) => {
    const index = timeIntervals.findIndex((event) => event.id === data.id);
    const oldEvent = timeIntervals[index];
    const newTimeIntervals = [...timeIntervals];
    newTimeIntervals[index] = { ...oldEvent, ...data };

    setTimeIntervals(newTimeIntervals);
  };

  return (
    <div className={styles.cssWrapper}>
      <FullCalendar
        plugins={[timeGridPlugin, interactionPlugin, dayGridPlugin]}
        headerToolbar={{
          start: "",
          center: "",
          end: "",
        }}
        dayHeaderContent={(obj) => <DayHeaderContent obj={obj} />}
        slotLabelContent={(obj) => <SlotLabelContent obj={obj} />}
        allDaySlot={false}
        nowIndicator={false}
        slotMinTime="00:00:00"
        slotMaxTime="24:00:00"
        slotDuration="00:15:00"
        expandRows={true}
        slotLabelInterval={{ hours: 1 }}
        editable={true}
        eventOverlap={false}
        dateClick={editable ? handleDateClick : () => null}
        eventClick={editable ? handleEventClick : () => null}
        eventResize={
          editable
            ? (e: EventResizeDoneArg) => onSaveEvent(getEventData(e))
            : () => null
        }
        eventDrop={
          editable
            ? (e: EventDropArg) => onSaveEvent(getEventData(e))
            : () => null
        }
        height="600px"
        events={timeIntervals}
      />
    </div>
  );
};

export default TimeIntervalPicker;
